/**
 * Chat API client with tool-use event support.
 *
 * Streams SSE events from POST /api/assistant/dashboard/chat/.
 * Handles: token, done, error, tool_start, tool_result, confirmation.
 */

import { getValidAccessTokenFromStorage } from "@weco/suite-client";
import type { Card, ChatErrorKind, SSEEvent } from "../types/chat";
import { resolveCurrentContext } from "../utils/resolveContext";

const CHAT_ENDPOINT = "/api/assistant/dashboard/chat/";

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getValidAccessTokenFromStorage();
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

/** The person's IANA time zone: the server counts the daily message limit
 * over their local day, not UTC's. */
export function timeZoneHeader(): Record<string, string> {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? { "X-Timezone": tz } : {};
  } catch {
    return {};
  }
}

/**
 * Turn a refusal the server sent before opening the stream into the message
 * shown to the person. The assistant answers `{error: {code, message,
 * details}}` for chat turned off (403 chat_disabled), no provider key (501
 * not_connected) and the daily limit (429 daily_limit) — its message is
 * already in Mochi's voice, so it is shown as-is. Older refusals still use
 * `{error: "..."}`.
 */
export function describeChatRefusal(
  status: number,
  body: unknown,
  retryAfter: string | null,
): { message: string; kind: ChatErrorKind } {
  const error = (body as { error?: unknown } | null)?.error;
  if (error && typeof error === "object" && typeof (error as { message?: unknown }).message === "string") {
    const { code, message } = error as { code?: string; message: string };
    return { message, kind: code === "daily_limit" ? "rate_limit" : "server" };
  }
  if (status === 429) {
    const wait = retryAfter ? ` Try again in ${retryAfter}s.` : "";
    return { message: `Rate limit reached.${wait}`, kind: "rate_limit" };
  }
  if (status === 409) {
    // services/assistant core/api/views.py: one reply in flight per session.
    return { message: "This conversation is still finishing a reply. Try again in a moment.", kind: "busy" };
  }
  let message = `Request failed (${status})`;
  if (typeof error === "string") message = error;
  else if (error) message = JSON.stringify(error);
  return { message, kind: "server" };
}

/**
 * Why a reply failed, so the Hub can explain it. "busy" is the backend's
 * one-reply-in-flight 409 (a previous reply in this session is still being
 * generated, e.g. right after Stop).
 */
export type { ChatErrorKind } from "../types/chat";

export interface StreamChatOptions {
  message: string;
  sessionId: string;
  confirmedToolCalls?: string[];
  attachmentId?: string;
  onToken: (delta: string) => void;
  onDone: (sessionId: string) => void;
  onError: (message: string, kind?: ChatErrorKind) => void;
  onToolStart?: (name: string, input: Record<string, unknown>) => void;
  onToolResult?: (name: string, output: string) => void;
  onConfirmation?: (toolName: string, toolId: string, input: Record<string, unknown>) => void;
  onCard?: (card: Card) => void;
  signal?: AbortSignal;
}

export async function streamChat(options: StreamChatOptions): Promise<void> {
  const {
    message, sessionId, confirmedToolCalls, attachmentId, onToken, onDone, onError,
    onToolStart, onToolResult, onConfirmation, onCard, signal,
  } = options;

  const context = resolveCurrentContext();
  const body: Record<string, unknown> = {
    message,
    session_id: sessionId,
    confirmed_tool_calls: confirmedToolCalls ?? [],
  };
  if (context) {
    body.context = context;
  }
  if (attachmentId) {
    body.attachment_id = attachmentId;
  }

  const response = await fetch(CHAT_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...timeZoneHeader(), ...await getAuthHeaders() },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch { /* not JSON */ }
    const { message: msg, kind } = describeChatRefusal(
      response.status, payload, response.headers.get("Retry-After"),
    );
    onError(msg, kind);
    return;
  }

  if (!response.body) {
    onError("Streaming not supported by this environment.");
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        // A frame can carry multiple fields (id:, data:, ...) on separate
        // lines — match per line, not against the whole frame, or every
        // event silently drops once the backend prefixes an id: line.
        for (const rawLine of part.split("\n")) {
          const line = rawLine.trim();
          if (!line.startsWith("data: ")) continue;

          let event: SSEEvent;
          try {
            event = JSON.parse(line.slice(6)) as SSEEvent;
          } catch {
            continue;
          }

          switch (event.type) {
            case "token":
              onToken(event.delta);
              break;
            case "done":
              onDone(event.session_id);
              break;
            case "error":
              onError(event.message, "server");
              break;
            case "tool_start":
              onToolStart?.(event.name, event.input);
              break;
            case "tool_result":
              onToolResult?.(event.name, event.output);
              break;
            case "card":
              onCard?.(event.card);
              break;
            case "confirmation":
              onConfirmation?.(event.tool_name, event.tool_id, event.input);
              break;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export interface ConfirmToolCallResult {
  tool_name: string;
  output: string;
  is_error: boolean;
}

const CONFIRM_ENDPOINT = "/api/assistant/dashboard/chat/confirm/";

export async function confirmToolCall(sessionId: string, toolId: string): Promise<ConfirmToolCallResult> {
  const response = await fetch(CONFIRM_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...await getAuthHeaders() },
    body: JSON.stringify({ session_id: sessionId, tool_id: toolId }),
  });

  if (!response.ok) {
    throw new Error(`Confirm request failed (${response.status})`);
  }

  return response.json();
}
